/**
 * Sequential Approval Flow Types
 *
 * Comprehensive TypeScript interfaces and type guards for the sequential approval system.
 * Provides null safety, validation, and proper error handling for approval workflows.
 */

import { MessagePart, ChatMessage } from "../utils/messageHelpers";

/**
 * Approval states in the sequential flow
 */
export type ApprovalState =
  | "pending" // Waiting in queue
  | "requesting" // Currently shown to user for approval
  | "approved" // User approved, executing
  | "denied" // User denied
  | "completed" // Successfully executed
  | "failed"; // Execution failed

/**
 * Core approval data structure
 */
export interface ApprovalData {
  readonly id: string;
  readonly toolName: string;
  readonly toolCallId: string;
  readonly input: Record<string, any>;
  readonly messageId: string;
  readonly state: ApprovalState;
  readonly queuePosition: number;
  readonly totalInQueue: number;
  readonly timestamp: number;
  readonly approved?: boolean;
  readonly errorText?: string;
  readonly successText?: string;
}

/**
 * Sequential approval queue item
 */
export interface ApprovalQueueItem {
  readonly approval: ApprovalData;
  readonly part: MessagePart;
  readonly textParts: MessagePart[];
}

/**
 * Sequential approval queue state
 */
export interface ApprovalQueueState {
  readonly items: ApprovalQueueItem[];
  readonly currentIndex: number;
  readonly totalCount: number;
  readonly isProcessing: boolean;
  readonly lastUpdated: number;
}

/**
 * Approval processing result
 */
export interface ApprovalProcessingResult {
  readonly success: boolean;
  readonly currentApproval: ApprovalData | null;
  readonly queueState: ApprovalQueueState;
  readonly error?: string;
}

/**
 * Type guard: Check if a value is a valid approval ID
 */
export function isValidApprovalId(value: unknown): value is string {
  return (
    typeof value === "string" && value.length > 0 && value.trim() === value
  );
}

/**
 * Type guard: Check if a value is a valid approval state
 */
export function isValidApprovalState(value: unknown): value is ApprovalState {
  const validStates: ApprovalState[] = [
    "pending",
    "requesting",
    "approved",
    "denied",
    "completed",
    "failed",
  ];
  return (
    typeof value === "string" && validStates.includes(value as ApprovalState)
  );
}

/**
 * Type guard: Check if a message part has approval data
 */
export function hasApprovalData(part: MessagePart): part is MessagePart & {
  approval: { id: string; approved?: boolean };
  type: string;
} {
  return !!(
    part &&
    typeof part === "object" &&
    part.type?.startsWith("tool-") &&
    part.approval &&
    typeof part.approval === "object" &&
    isValidApprovalId(part.approval.id)
  );
}

/**
 * Type guard: Check if a message part is an approval request
 */
export function isApprovalRequest(part: MessagePart): boolean {
  try {
    return hasApprovalData(part) && part.state === "approval-requested";
  } catch {
    return false;
  }
}

/**
 * Type guard: Check if a message part is an approval response
 */
export function isApprovalResponse(part: MessagePart): boolean {
  try {
    return hasApprovalData(part) && part.state === "approval-responded";
  } catch {
    return false;
  }
}

/**
 * Type guard: Check if an approval data object is valid
 */
export function isValidApprovalData(value: unknown): value is ApprovalData {
  if (!value || typeof value !== "object") return false;

  const data = value as Partial<ApprovalData>;

  return !!(
    isValidApprovalId(data.id) &&
    typeof data.toolName === "string" &&
    data.toolName.length > 0 &&
    typeof data.toolCallId === "string" &&
    data.toolCallId.length > 0 &&
    typeof data.input === "object" &&
    data.input !== null &&
    typeof data.messageId === "string" &&
    data.messageId.length > 0 &&
    isValidApprovalState(data.state) &&
    typeof data.queuePosition === "number" &&
    data.queuePosition >= 0 &&
    typeof data.totalInQueue === "number" &&
    data.totalInQueue > 0 &&
    typeof data.timestamp === "number" &&
    data.timestamp > 0
  );
}

/**
 * Type guard: Check if a queue state is valid
 */
export function isValidQueueState(value: unknown): value is ApprovalQueueState {
  if (!value || typeof value !== "object") return false;

  const state = value as Partial<ApprovalQueueState>;

  return !!(
    Array.isArray(state.items) &&
    typeof state.currentIndex === "number" &&
    state.currentIndex >= -1 &&
    typeof state.totalCount === "number" &&
    state.totalCount >= 0 &&
    typeof state.isProcessing === "boolean" &&
    typeof state.lastUpdated === "number" &&
    state.lastUpdated > 0
  );
}

/**
 * Safe approval data creator with validation
 */
export function createApprovalData(
  id: string,
  toolName: string,
  toolCallId: string,
  input: Record<string, any>,
  messageId: string,
  state: ApprovalState,
  queuePosition: number,
  totalInQueue: number,
  options: {
    approved?: boolean;
    errorText?: string;
    successText?: string;
  } = {}
): ApprovalData | null {
  try {
    if (!isValidApprovalId(id)) return null;
    if (!toolName || typeof toolName !== "string") return null;
    if (!toolCallId || typeof toolCallId !== "string") return null;
    if (!input || typeof input !== "object") return null;
    if (!messageId || typeof messageId !== "string") return null;
    if (!isValidApprovalState(state)) return null;
    if (typeof queuePosition !== "number" || queuePosition < 0) return null;
    if (typeof totalInQueue !== "number" || totalInQueue < 1) return null;

    const approvalData: ApprovalData = {
      id,
      toolName,
      toolCallId,
      input: { ...input }, // Shallow copy for safety
      messageId,
      state,
      queuePosition,
      totalInQueue,
      timestamp: Date.now(),
      ...options,
    };

    return isValidApprovalData(approvalData) ? approvalData : null;
  } catch (error) {
    console.error("[createApprovalData] Validation failed:", error);
    return null;
  }
}

/**
 * Safe queue state creator with validation
 */
export function createQueueState(
  items: ApprovalQueueItem[],
  currentIndex: number,
  isProcessing: boolean = false
): ApprovalQueueState | null {
  try {
    if (!Array.isArray(items)) return null;
    if (typeof currentIndex !== "number" || currentIndex < -1) return null;
    if (typeof isProcessing !== "boolean") return null;

    const queueState: ApprovalQueueState = {
      items: [...items], // Shallow copy for safety
      currentIndex,
      totalCount: items.length,
      isProcessing,
      lastUpdated: Date.now(),
    };

    return isValidQueueState(queueState) ? queueState : null;
  } catch (error) {
    console.error("[createQueueState] Validation failed:", error);
    return null;
  }
}

/**
 * Extract tool name safely from message part type
 */
export function safeExtractToolName(part: MessagePart): string | null {
  try {
    if (!part?.type || typeof part.type !== "string") return null;
    if (!part.type.startsWith("tool-")) return null;

    const toolName = part.type.replace("tool-", "");
    return toolName.length > 0 ? toolName : null;
  } catch {
    return null;
  }
}

/**
 * Safely extract approval ID from message part
 */
export function safeExtractApprovalId(part: MessagePart): string | null {
  try {
    if (!hasApprovalData(part)) return null;
    return part.approval.id;
  } catch {
    return null;
  }
}

/**
 * Error types for approval processing
 */
export class ApprovalError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly approvalId?: string
  ) {
    super(message);
    this.name = "ApprovalError";
  }
}

export class ApprovalValidationError extends ApprovalError {
  constructor(message: string, approvalId?: string) {
    super(message, "VALIDATION_ERROR", approvalId);
    this.name = "ApprovalValidationError";
  }
}

export class ApprovalProcessingError extends ApprovalError {
  constructor(message: string, approvalId?: string) {
    super(message, "PROCESSING_ERROR", approvalId);
    this.name = "ApprovalProcessingError";
  }
}

/**
 * Safe error handler for approval operations
 */
export function handleApprovalError(
  error: unknown,
  context: string
): ApprovalError {
  if (error instanceof ApprovalError) {
    return error;
  }

  const message = error instanceof Error ? error.message : "Unknown error";
  console.error(`[${context}] Approval error:`, error);

  return new ApprovalProcessingError(`${context}: ${message}`);
}
